import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { builderDispatch, WebUI } from '../../_utils/ui';

import {
  usePrintsOrderStore,
  usePrintsUiStore,
  useAdvancedEditorStore,
  usePrintsConfigStore,
} from '../stores';

import {
  UpdatePhotoEvents,
  DefaultsEvents,
} from '../common/events/event-declarations';

const normalizeToArray = (value) =>
  value == null ? [] : Array.isArray(value) ? value : [value];

/**
 * A non-visual component that acts as an adapter, listening for legacy
 * builderDispatch events and translating them into actions on the Zustand store.
 * This should normalise IDs from frontend components that send events with single IDs or arrays.
 */
const LegacyEventHandler = () => {
  // --- STATE ---
  const { debugEnabled } = usePrintsConfigStore((state) => state.features);
  const pendingUiEvent = usePrintsUiStore((state) => state.pendingUiEvent);

  // --- ACTIONS ---
  const {
    removePhotoFromSize,
    removePhotoByRoot,
    updatePhoto,
    setPhotoQuantity,
    singlePhotoResize,
  } = usePrintsOrderStore(
    useShallow((state) => ({
      removePhotoFromSize: state.removePhotoFromSize,
      removePhotoByRoot: state.removePhotoByRoot,
      updatePhoto: state.updatePhoto,
      setPhotoQuantity: state.setPhotoQuantity,
      singlePhotoResize: state.singlePhotoResize,
    })),
  );
  const { handleCartMerged } = usePrintsConfigStore(
    useShallow((state) => ({
      handleCartMerged: state.handleCartMerged,
    })),
  );
  const {
    clearPendingUiEvent,
    setSelectedUploadSizeKey,
    setViewingSizeKey,
    toggleDuplicatePhotoView,
  } = usePrintsUiStore(
    useShallow((state) => ({
      clearPendingUiEvent: state.clearPendingUiEvent,
      setSelectedUploadSizeKey: state.setSelectedUploadSizeKey,
      setViewingSizeKey: state.setViewingSizeKey,
      toggleDuplicatePhotoView: state.toggleDuplicatePhotoView,
    })),
  );

  const { openEditor, closeEditor } = useAdvancedEditorStore(
    useShallow((state) => ({
      openEditor: state.openEditor,
      closeEditor: state.closeEditor,
    })),
  );

  const processRemoval = (payload) => {
    const { useRoot, clientUuid, clientUuids } = payload;

    if (useRoot) {
      removePhotoByRoot({ clientUuid });
    } else {
      removePhotoFromSize({ clientUuids: clientUuids ?? [clientUuid] });
    }
  };

  useEffect(() => {
    const token = builderDispatch.register((payload) => {
      if (debugEnabled) {
        console.log(`LegacyEventHandler.${payload.type}`, { payload }); // eslint-disable-line no-console
      }

      switch (payload.type) {
        case UpdatePhotoEvents.REMOVE:
          processRemoval(payload);
          break;

        case UpdatePhotoEvents.UPDATE: {
          // payload.photo can be an array of photos or single object depending on
          // source of the event. Decrementing to zero is always a single photo
          const photo = Array.isArray(payload.photo)
            ? payload.photo[0]
            : payload.photo;

          if (!photo) {
            return;
          }

          const isDecrementingToZero = photo.quantity === 0;

          if (isDecrementingToZero) {
            processRemoval({
              useRoot: true,
              clientUuid: photo.client_uuid,
            });
          } else {
            updatePhoto({
              updatedPhotos: normalizeToArray(payload.photo),
              shouldSave: payload.hard,
            });
          }
          break;
        }

        case UpdatePhotoEvents.ADD_TO_SIZE: {
          const { rootImageId, targetSizeKey, quantity: newQuantity } = payload;
          const rootImageIds = normalizeToArray(rootImageId); // rootImageId is a single ID when used per photo or an array of IDs from {Bulk}QuantityChange

          setPhotoQuantity({ rootImageIds, targetSizeKey, newQuantity });
          break;
        }

        case UpdatePhotoEvents.RESIZE: {
          const { clientUuid, targetSizeKey } = payload;

          singlePhotoResize({ clientUuid, targetSizeKey });
          setViewingSizeKey(targetSizeKey);
          break;
        }

        case UpdatePhotoEvents.TOGGLE_DUPLICATE_PHOTO_VIEW: {
          toggleDuplicatePhotoView();
          break;
        }

        case UpdatePhotoEvents.ADVANCED_EDIT: {
          const { clientUuid, isLastPhoto } = payload;

          openEditor({ clientUuid, isLastPhoto });
          break;
        }

        case UpdatePhotoEvents.ADVANCED_EDIT_CLOSE: {
          // This event is actually advanced editor HAS closed, so we need to update state to match
          closeEditor();
          break;
        }

        case DefaultsEvents.CHANGE_SIZE: {
          setSelectedUploadSizeKey(payload.sizeKey);
          break;
        }

        case WebUI.CART_MERGED: {
          handleCartMerged();
          break;
        }
      }
    });

    return () => {
      builderDispatch.unregister(token);
    };
  }, [
    removePhotoFromSize,
    removePhotoByRoot,
    updatePhoto,
    setPhotoQuantity,
    singlePhotoResize,
    openEditor,
    closeEditor,
    toggleDuplicatePhotoView,
    setViewingSizeKey,
    pendingUiEvent,
    clearPendingUiEvent,
    setSelectedUploadSizeKey,
    handleCartMerged,
    debugEnabled,
  ]);

  // Respond to pendingUiEvent changes by triggering a legacy event
  // Only needed while we have components that need to respond to legacy events
  useEffect(() => {
    if (pendingUiEvent) {
      builderDispatch.delayedDispatch({
        type: pendingUiEvent.type,
        ...pendingUiEvent.payload,
      });
      clearPendingUiEvent();
    }
  }, [pendingUiEvent, clearPendingUiEvent]);

  return null;
};

export default LegacyEventHandler;
